import { gql, useLazyQuery, useQuery } from '@apollo/client'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'

import { useGetNewestData } from '@/clients/utoniq-core'
import {
  ConsoleOrdersQuery,
  ConsoleOrdersQueryVariables,
  CsvDownload,
  FetchNewConsoleOrdersQuery,
  FetchNewConsoleOrdersQueryVariables,
  OrderBy,
  UserCartTransactionOrderBoxFragmentDoc,
  UserCartTransactionOrderConnectionFragment,
  UserCartTransactionOrderConnectionFragmentDoc,
} from '@/clients/utoniq-core/schema'
import { useMessage, useTranslation } from '@/core'
import { makeViewModel } from '@/core/architecture'
import { useCurrentUser } from '@/core/auth'
import { getPrefrenceObject, setPreference } from '@/core/native/storage'
import { useDownloadFile } from '@/lib/downloadFile'

import { Action, State } from './template'

/**
 *
 */
export const useViewModel = makeViewModel<State, Action>(() => {
  const { t } = useTranslation('consoleOrders')
  const { user } = useCurrentUser()
  const { showError, showMessage } = useMessage()
  const { downloadCsv } = useDownloadFile()
  const params = new URLSearchParams(window.location.search)
  const sort = params.get('sort') === OrderBy.Asc ? OrderBy.Asc : OrderBy.Desc
  const [orderBy, setOrderBy] = useState<OrderBy>(sort)

  const [isExporting, setIsExporting] = useState(false)
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: 'success' | 'error'
  }>({
    open: false,
    message: '',
    severity: 'success',
  })

  type ConsoleOrdersDate = {
    startDate: string
    endDate: string
  }

  // ローカルストレージから初期値を取得
  const getStoredDate = async () => {
    return await getPrefrenceObject<ConsoleOrdersDate>('consoleOrdersDate')
  }

  const [startDate, setStartDate] = useState<Date | undefined>(undefined)
  const [endDate, setEndDate] = useState<Date | undefined>(undefined)

  // 初期値の読み込み
  useEffect(() => {
    const loadDates = async () => {
      const storedDate = await getStoredDate()

      const storedStartDate = storedDate?.startDate
        ? dayjs(storedDate.startDate).toDate()
        : undefined
      const storedEndDate = storedDate?.endDate
        ? dayjs(storedDate.endDate).toDate()
        : undefined

      setStartDate(storedStartDate)
      setEndDate(storedEndDate)
    }
    loadDates()
  }, [])

  const { isFirstLoadRef } = useGetNewestData<
    FetchNewConsoleOrdersQuery,
    FetchNewConsoleOrdersQueryVariables
  >({
    query: FETCH_NEW_ORDERS,
    variables: { orderBy: orderBy, startDate: startDate, endDate: endDate },
    fields: [
      {
        fragmentName: 'UserCartTransactionOrderBox',
        fieldName: 'artistUserCartTransactionOrders',
        fragment: UserCartTransactionOrderBoxFragmentDoc,
      },
    ],
  })

  const [orderTransactions, setOrderTransactions] =
    useState<UserCartTransactionOrderConnectionFragment | null>(null)

  const { data, loading, fetchMore, refetch } = useQuery<
    ConsoleOrdersQuery,
    ConsoleOrdersQueryVariables
  >(QUERY, {
    variables: { orderBy: orderBy, startDate: startDate, endDate: endDate },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    onCompleted(data) {
      setOrderTransactions(data?.artistUserCartTransactionOrders ?? null)
      isFirstLoadRef.current = false
    },
  })

  const isNextFetch = !!data

  const [artistExportCartTransactionOrdersCsv] = useLazyQuery<
    {
      artistExportCartTransactionOrdersCsv: Pick<CsvDownload, 'csvUrl'>
    },
    { startDate?: Date; endDate?: Date }
  >(EXPORT_CSV_QUERY, {
    async onCompleted({ artistExportCartTransactionOrdersCsv }) {
      if (!artistExportCartTransactionOrdersCsv?.csvUrl) {
        return showError(new Error(t('downloadCsvError')))
      }
      await downloadCsv(artistExportCartTransactionOrdersCsv.csvUrl).then(
        () => {
          showMessage(t('downloadCsvSuccess'), 'success')
        }
      )
    },
    onError: error => {
      if (error?.graphQLErrors?.[0]?.extensions?.errorCode === 'NO_DATA') {
        showError(new Error(t('noData')))
      } else {
        showError(new Error(t('downloadCsvError')))
      }
    },
  })

  const handleExportCsv = async () => {
    setIsExporting(true)
    await artistExportCartTransactionOrdersCsv({
      variables: {
        startDate,
        endDate,
      },
    }).finally(() => {
      setIsExporting(false)
    })
  }

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }))
  }

  return {
    state: {
      orderTransactions: orderTransactions,
      loading: loading,
      artistId: user?.uid ?? '',
      user: user,
      artist: data?.artist ?? null,
      hasNextPage: !!data?.artistUserCartTransactionOrders.pageInfo.hasNextPage,
      sort: orderBy,
      membership: data?.artist?.subscription?.products ?? undefined,
      startDate: startDate ?? undefined,
      endDate: endDate ?? undefined,
      isExporting,
      snackbar,
    },
    actions: {
      async onRefetch(orderBy, inputStartDate?, inputEndDate?) {
        setOrderTransactions(null)
        setOrderBy(orderBy)
        setStartDate(inputStartDate)
        setEndDate(inputEndDate)
        await setPreference('consoleOrdersDate', {
          startDate: inputStartDate ? inputStartDate.toISOString() : undefined,
          endDate: inputEndDate ? inputEndDate.toISOString() : undefined,
        })
        await refetch({
          orderBy,
          startDate: inputStartDate,
          endDate: inputEndDate,
        })
      },
      async onFetchMore() {
        await fetchMore({
          variables: {
            orderBy,
            pageInput: {
              cursor: data?.artistUserCartTransactionOrders?.pageInfo.endCursor,
            },
          },
        })
      },
      onExportCsv: handleExportCsv,
      onCloseSnackbar: handleCloseSnackbar,
    },
  }
})

const QUERY = gql`
  query ConsoleOrders(
    $orderBy: OrderBy
    $pageInput: PageInput
    $userId: String
    $startDate: Date
    $endDate: Date
  ) {
    artistUserCartTransactionOrders(
      orderBy: $orderBy
      pageInput: $pageInput
      userId: $userId
      startDate: $startDate
      endDate: $endDate
    ) {
      ...UserCartTransactionOrderConnection
    }
    artist {
      id
      paymentType
      hasArrangingDeliveryItems
      subscription {
        products {
          productId
        }
      }
    }
  }
  ${UserCartTransactionOrderConnectionFragmentDoc}
`

const FETCH_NEW_ORDERS = gql`
  query FetchNewConsoleOrders(
    $orderBy: OrderBy
    $pageInput: PageInput
    $userId: String
    $startDate: Date
    $endDate: Date
  ) {
    artistUserCartTransactionOrders(
      orderBy: $orderBy
      pageInput: $pageInput
      userId: $userId
      startDate: $startDate
      endDate: $endDate
    ) {
      ...UserCartTransactionOrderConnection
    }
  }
  ${UserCartTransactionOrderConnectionFragmentDoc}
`

const EXPORT_CSV_QUERY = gql`
  query ExportCsv($startDate: Date, $endDate: Date) {
    artistExportCartTransactionOrdersCsv(
      startDate: $startDate
      endDate: $endDate
    ) {
      csvUrl
    }
  }
`
